import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSession } from '../context/SessionContext';
import '../styles/review.css';

const Review = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { session, generateProjectBrief } = useSession();
  
  // Generate project brief when component mounts
  useEffect(() => {
    if (session && !session.projectBrief) {
      generateProjectBrief();
    }
  }, [session, generateProjectBrief]);

  // Generate project brief from answers
  const generateLocalBrief = () => {
    const questions = session?.questions || [];
    const answers = session?.answers || {};
    
    // Debug logging
    console.log('🔍 Session data:', { questions: questions.length, answers: Object.keys(answers) });
    console.log('📝 All answers:', answers);
    
    const getAnswer = (questionId) => {
      // First try to get from answers object
      const answerFromAnswers = answers[questionId];
      if (answerFromAnswers) {
        if (answerFromAnswers.value !== undefined) {
          const value = answerFromAnswers.value;
          if (Array.isArray(value)) {
            return value.join(', ');
          }
          return value || 'Not specified';
        }
        return answerFromAnswers;
      }
      
      // Fallback to question.answer
      const question = questions.find(q => q.id === questionId);
      const answer = question?.answer;
      
      if (answer && typeof answer === 'object' && answer.value !== undefined) {
        if (Array.isArray(answer.value)) {
          return answer.value.join(', ');
        }
        return answer.value || 'Not specified';
      }
      
      if (Array.isArray(answer)) {
        return answer.join(', ');
      }
      return answer || 'Not specified';
    };

    return {
      businessModel: getAnswer('business-model'),
      scaleTier: getAnswer('scale-tier'),
      availability: getAnswer('availability-slo'),
      compliance: getAnswer('compliance-requirements'),
      dataTypes: getAnswer('data-sensitivity'),
      authentication: getAnswer('authentication-model'),
      dataModel: getAnswer('data-model'),
      integrations: getAnswer('external-integrations'),
      deployment: getAnswer('deployment-strategy'),
      maturity: getAnswer('operational-maturity')
    };
  };

  const brief = generateLocalBrief();

  const handleConfirmBrief = () => {
    // Ensure project brief is generated before navigating
    if (!session?.projectBrief) {
      generateProjectBrief();
    }
    navigate('/artifacts');
  };

  const handleEditAnswers = () => {
    navigate(`/project/${sessionId}/gather`);
  };

  return (
    <div className="review-container">
      <header className="review-header">
        <h1>Project Brief</h1>
        <p>Based on your answers, here's what we understand about {session?.projectName || 'your project'}:</p>
      </header>

      <div className="project-brief">
        <div className="brief-section project-overview">
          <h3>Project Overview</h3>
          <div className="brief-item">
            <strong>Project Name:</strong> {session?.projectName || 'Not specified'}
          </div>
          <div className="brief-item">
            <strong>Description:</strong> {session?.initialIdea || 'A high-volume, real-time prediction platform for sports, entertainment, and financial events without real money wagering.'}
          </div>
        </div>

        <div className="brief-section">
          <h3>Business Context</h3>
          <div className="brief-item">
            <strong>Business Model:</strong> {brief.businessModel}
          </div>
          <div className="brief-item">
            <strong>Scale & Risk Profile:</strong> {brief.scaleTier}
          </div>
          <div className="brief-item">
            <strong>Compliance Requirements:</strong> {brief.compliance}
          </div>
        </div>

        <div className="brief-section">
          <h3>Technical Requirements</h3>
          <div className="brief-item">
            <strong>Availability Target:</strong> {brief.availability}
          </div>
          <div className="brief-item">
            <strong>Data Sensitivity:</strong> {brief.dataTypes}
          </div>
          <div className="brief-item">
            <strong>Authentication:</strong> {brief.authentication}
          </div>
        </div>

        <div className="brief-section">
          <h3>Architecture Approach</h3>
          <div className="brief-item">
            <strong>Core Data Model:</strong> {brief.dataModel}
          </div>
          <div className="brief-item">
            <strong>External Integrations:</strong> {brief.integrations}
          </div>
          <div className="brief-item">
            <strong>Deployment Strategy:</strong> {brief.deployment}
          </div>
          <div className="brief-item">
            <strong>Operational Maturity:</strong> {brief.maturity}
          </div>
        </div>
      </div>

      <div className="confirmation-section">
        <h2>Did we get this right?</h2>
        <p>Please review the summary above. You can make corrections or proceed to see our recommended artifacts.</p>
        
        <div className="confirmation-actions">
          <button className="button-secondary" onClick={handleEditAnswers}>
            Edit My Answers
          </button>
          <button className="button-enabled" onClick={handleConfirmBrief}>
            Yes, This Looks Good
          </button>
        </div>
      </div>
    </div>
  );
};

export default Review;